/* eslint-disable prettier/prettier */
import { CommonModule, DatePipe } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { HttpErrorResponse } from "@angular/common/http";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDividerModule } from "@angular/material/divider";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDatepickerModule } from "@angular/material/datepicker";
import {
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
  MatNativeDateModule,
} from "@angular/material/core";
import { ActivatedRoute } from "@angular/router";
import { merge, Observable, Subject, of, firstValueFrom } from "rxjs";
import {
  catchError,
  debounceTime,
  map,
  switchMap,
  takeUntil,
} from "rxjs/operators";

import {
  DocumentDetailsRecord,
  getTemplateForLanguage,
  DocumentTemplate,
  DocumentStatus,
  upsertTemplateForLanguage,
} from "../../document-details.model";
import { DocumentDetailsService } from "../../document-details.service";
import { LanguageService } from "app/language/language.service";
import { OrderService } from "app/orders/order.service";
import { DocumentService } from "../../document.service";
import { Document, DocumentType } from "../../documents";
import {
  CERTIFICATE_OF_CONFORMITY_VARIABLES,
  CERTIFICATE_OF_CONFORMITY_GROUP_LABELS,
  CertificateOfConformityVariable,
  buildProducerConversionKey,
} from "../certificate-of-conformity-variables.constants";
import {
  resolveWheelbaseFormValues,
  getWheelbaseSelectOptions,
  getWheelbaseReferenceTable,
} from "../utils/wheelbase/wheelbase-utils";
import { resolveVehicleClass } from "../utils/vehicle-class-utils";
import { VehicleLine, resolveVehicleLine } from "../utils/vehicle-line";
import {
  getMassData,
  hasMassDataTable,
} from "../utils/mass-data/vehicle-mass-data";
import {
  getAxleMassLimit,
  hasAxleMassLimitTable,
} from "../utils/axle-mass-limits/vehicle-axle-mass-limits";
import { extractGearboxMarker } from "../utils/axle-mass-limits/iveco-axle-mass-limits";
import {
  isTowHookSelected,
  resolveTowHookMassValues,
  type TowHookMassValues,
} from "../utils/tow-hook";
import {
  getMercedesTouristLineMassCode,
  getMercedesTouristLinePassengersCode,
  getMercedesTouristLineWheelchairCode,
} from "../utils/mercedes-tourist-line-version";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-certificate-of-conformity-document",
  standalone: true,
  providers: [DatePipe],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatDividerModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  templateUrl: "./certificate-of-conformity-document.component.html",
  styleUrls: ["./certificate-of-conformity-document.component.css"],
})
export class CertificateOfConformityDocumentComponent
  implements OnInit, OnDestroy
{
  private destroy$ = new Subject<void>();
  /** Eksponowane stałe zmiennych dla szablonu HTML */
  readonly variables: CertificateOfConformityVariable[] =
    CERTIFICATE_OF_CONFORMITY_VARIABLES;

  /** Zmienne wymagające osobnego tłumaczenia per język */
  readonly requiredTranslationVariables: CertificateOfConformityVariable[] =
    CERTIFICATE_OF_CONFORMITY_VARIABLES.filter(
      (variable) => variable.requiredTranslation,
    );

  /** Klucz meta dla obiektu tłumaczeń w fieldValues */
  private readonly REQUIRED_TRANSLATIONS_META_KEY = "translations";

  /** Pamięć podręczna tłumaczeń per język: { PL: { commercialName: "..." }, EN: {...} } */
  private translationsByLanguage: Record<string, Record<string, any>> = {};

  /** Grupowanie zmiennych według sekcji numerycznych z dokumentu (tylko pola widoczne) */
  readonly groupedVariables: {
    group: CertificateOfConformityVariable["group"];
    label: string;
    variables: CertificateOfConformityVariable[];
  }[] = this.createGroupedVariables();

  /**
   * Grupowanie zmiennych ukrytych (hidden: true) według tych samych sekcji.
   * Prezentowane w formularzu w zwijanej sekcji "Pola domyślnie ukryte",
   * żeby główny formularz pozostał zwięzły, a jednocześnie dało się podejrzeć
   * i w razie potrzeby skorygować wartości pól niewidocznych na co dzień.
   */
  readonly groupedHiddenVariables: {
    group: CertificateOfConformityVariable["group"];
    label: string;
    variables: CertificateOfConformityVariable[];
  }[] = this.createGroupedHiddenVariables();

  fieldValues: any = {};

  /**
   * Cache wartości wyznaczonych automatycznie przy wczytaniu zamówienia
   * (formatter: computed, value "na sztywno", orderPath, rozstaw osi).
   * Odświeżany tylko w autoFillFromOrder (nie przy każdej zmianie w formularzu),
   * żeby pole disabled nie blokowało się w trakcie ręcznego wpisywania wartości.
   * Używany w isVariableDisabled do synchronicznego sprawdzenia, czy pole ma
   * już jakąkolwiek automatycznie ustaloną wartość.
   */
  private autoResolvedValuesCache: Record<string, any> = {};

  private createGroupedVariables() {
    const groups = Object.keys(
      CERTIFICATE_OF_CONFORMITY_GROUP_LABELS,
    ) as CertificateOfConformityVariable["group"][];

    return groups
      .map((group) => ({
        group,
        label: CERTIFICATE_OF_CONFORMITY_GROUP_LABELS[group],
        variables: CERTIFICATE_OF_CONFORMITY_VARIABLES.filter(
          (variable) => variable.group === group && !variable.hidden,
        ),
      }))
      .filter((g) => g.variables.length > 0);
  }

  /**
   * Buduje grupowanie zmiennych ukrytych (hidden: true) - analogicznie do
   * createGroupedVariables, ale odwrotny filtr widoczności.
   * Podpola wersji (versionTonnage, versionWheelbase itd.) są pomijane, bo mają
   * już dedykowaną, warunkową prezentację w sekcji 0.2 (visibleVersionSubfields) -
   * pokazywanie ich też tutaj byłoby zbędnym powielaniem tego samego pola.
   */
  private createGroupedHiddenVariables() {
    const groups = Object.keys(
      CERTIFICATE_OF_CONFORMITY_GROUP_LABELS,
    ) as CertificateOfConformityVariable["group"][];

    return groups
      .map((group) => ({
        group,
        label: CERTIFICATE_OF_CONFORMITY_GROUP_LABELS[group],
        variables: CERTIFICATE_OF_CONFORMITY_VARIABLES.filter(
          (variable) =>
            variable.group === group &&
            !!variable.hidden &&
            !this.isVersionSubfield(variable),
        ),
      }))
      .filter((g) => g.variables.length > 0);
  }

  /** Ścieżka do grafiki podpisu zastępującej {{podpis}} przy druku */
  readonly SIGNATURE_IMAGE_PATH =
    "assets/images/certificate-of-conformity-document/podpis-coc.png";
  /** Grafika nagłówka druku – logo firmy wyrównane do lewej, maks. 90px wysokości */
  readonly PRINT_HEADER_IMAGE_PATH = "assets/images/logo.svg";
  /** Globalna wysokość nagłówka druku (w mm) */
  readonly PRINT_HEADER_HEIGHT_MM = 26;
  /** Maksymalna wysokość logo w nagłówku (w mm) */
  readonly PRINT_HEADER_LOGO_MAX_HEIGHT_MM = 14;
  /** Globalna wysokość stopki druku (w mm) */
  readonly PRINT_FOOTER_HEIGHT_MM = 20;
  /** Maksymalna liczba linii treści stopki */
  readonly PRINT_FOOTER_MAX_LINES = 4;
  /** Dodatkowy bezpieczny odstęp między treścią a nagłówkiem/stopką (w mm) */
  readonly PRINT_CONTENT_SAFE_GAP_MM = 2;
  /** Zawartość teleadresowa wyświetlana po prawej stronie nagłówka (4 wiersze) */
  readonly PRINT_HEADER_CONTACT_HTML = `
    <p>Auto-CUBY Sp. z o.o.</p>
    <p>KRS: 0000382450 | REGON: 221203580</p>
    <p>NIP: 588 237 59 04</p>
    <p>ul. Przemysłowa 2, 84-241 Gościcino</p>
  `;
  /**
   * Zawartość HTML stopki wydruku.
   */
  readonly PRINT_FOOTER_HTML = `
    <p>Auto-CUBY Sp. z o.o., ul. Przemysłowa 2, 84-241 Gościcino</p>
    <p>NIP: 588 237 59 04 &nbsp;|&nbsp; KRS: 0000382450 &nbsp;|&nbsp; Regon: 221203580</p>
    <p>tel. +48 509 388 082 &nbsp;|&nbsp; sekretariat@autocuby.pl &nbsp;|&nbsp; www.autocuby.eu</p>
  `;

  /**
   * Wspólne style CSS dla dokumentów (podgląd i wydruk).
   */
  readonly DOCUMENT_STYLES = `
    /* Reset marginesów dla paragrafów */
    p { line-height: 1; margin-block-start: 0; margin-block-end: 0; }
    
    /* Style dla nagłówka dokumentu */
    .print-header { overflow: hidden; border-bottom: 1px solid #e0e0e0; margin-bottom: 10mm; }
    .print-header-inner { display: flex; align-items: center; justify-content: space-between; gap: 6mm; }
    .print-header-logo img { max-height: ${this.PRINT_HEADER_LOGO_MAX_HEIGHT_MM}mm; width: auto; display: block; }
    .print-header-contact { text-align: right; font-size: 7.3pt; line-height: 1.22; color: #444; }
    .print-header-contact p { margin: 0; }
    
    /* Style dla stopki */
    .print-footer { margin-top: 10mm; padding-top: 5mm; border-top: 1px solid #e0e0e0; font-size: 7.3pt; color: #444; text-align: center; }
    .print-footer p { margin: 2px 0; }

    .general-construction td {
    padding: 0px 6px;
    }

    @media print {
  .page-break {
    break-before: page;             /* nowocześniejsze */
    page-break-before: always;      /* fallback dla starszych przeglądarek */
  }
      td {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
  `;

  form: FormGroup;
  orderId: string | null = null;
  order: any = null;
  languages: any[] = [];

  isLoadingOrder = false;
  isLoadingDocument = false;
  isSaving = false;
  isResetting = false;
  /** Czy dokument został zapisany i nie było późniejszych zmian – warunkuje widoczność przycisku Drukuj */
  canPrint = false;
  /** Czy użytkownik wprowadził zmiany w polach zmiennych od ostatniego zapisu */
  hasUnsavedChanges = false;
  /** Rola zalogowanego użytkownika (do sprawdzenia uprawnień superadmin) */
  userRole: string | null = null;

  /** Status dokumentu wczytany z rekordu (np. { name: "hardened", code: "Utwardzony" }) */
  private documentStatus: DocumentStatus | null = null;

  /** Czy dokument został utwardzony - blokuje zapis i edycję wszystkich pól */
  get isHardened(): boolean {
    return this.documentStatus?.name === "hardened";
  }

  /** Surowy wyrenderowany HTML (do druku i zapisu) */
  private rawRenderedHtml: string | null = null;

  /** Bezpieczny HTML podglądu wyświetlany w iframe pod formularzem */
  previewHtml: SafeHtml | null = null;
  /** Czy sekcja iframe podglądu jest widoczna */
  isPreviewVisible = false;

  /** Ostatnio pobrany szablon (do ponownego użycia przy zapisie bez nowego podglądu) */
  private currentDocument: Document | null = null;

  /**
   * Pamięć robocza wartości pól per język (na potrzeby przełączania języków bez utraty danych).
   */
  private draftValuesByLanguage: Record<string, Record<string, any>> = {};
  private persistedOrderDerivedValuesByLanguage: Record<
    string,
    Record<string, any>
  > = {};
  private activeLanguage: string | null = null;

  /**
   * Zapamiętuje obiekt translations z ostatnio zapisanego rekordu.
   * Używane przy przywracaniu wartości dla pól requiredTranslation.
   */
  private persistedTranslations: Record<string, Record<string, any>> = {};

  /**
   * Przechowuje istniejące szablony z innych języków pobrane podczas ładowania dokumentu.
   * Używane przy zapisie, aby nie nadpisywać szablonów innych języków.
   */
  private existingTemplates: DocumentTemplate[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private orderService: OrderService,
    private languageService: LanguageService,
    private documentService: DocumentService,
    private sanitizer: DomSanitizer,
    private documentDetailsService: DocumentDetailsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private _translateService: TranslateService,
    private datePipe: DatePipe,
  ) {
    this.form = this.buildForm();
  }

  ngOnInit(): void {
    this.loadLanguages();
    this.userRole = localStorage.getItem("role");
    this.applyVariableControlStates();
    this.orderId = this.route.snapshot.queryParamMap.get("orderId");

    if (this.orderId) {
      this.loadOrder(this.orderId);
    }

    this.form.get("language")?.valueChanges.subscribe((language: string) => {
      if (
        this.hasUnsavedChanges &&
        this.activeLanguage &&
        language !== this.activeLanguage
      ) {
        this.form.patchValue(
          { language: this.activeLanguage },
          { emitEvent: false },
        );
        return;
      }
      this.onLanguageChanged(language);
    });

    // Live-update podglądu iframe + reset canPrint tylko przy zmianie pól zmiennych
    const variableControls$ = CERTIFICATE_OF_CONFORMITY_VARIABLES.map(
      (v) => this.form.get(this.toControlName(v.key))?.valueChanges,
    ).filter((obs): obs is Observable<any> => !!obs);

    merge(...variableControls$)
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => {
        this.hasUnsavedChanges = true;
        this.canPrint = false;
        if (this.isPreviewVisible && this.currentDocument) {
          this.refreshPreview();
        }
      });

    // Subskrypcja na zmiany podpól wersji - aktualizacja computed version
    // TEMPORARY DISABLED - checking for infinite loop
    this.subscribeToVersionSubfields();

    // 13.2 musi być >= 13 - walidacja zależy od obu pól, więc zmiana 13
    // wymusza ponowne sprawdzenie 13.2
    this.form
      .get("actualMass")
      ?.setValidators(this.actualMassNotLowerThanRunningOrderValidator());
    this.form
      .get("massInRunningOrder")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.revalidateActualMass());
  }

  /**
   * Subskrybuje zmiany w podpolach wersji i aktualizuje główne pole {{version}}.
   * Dla zabudów, dla których mamy źródło danych masowych (obecnie: Iveco
   * Tourist Line, Mercedes Benz Tourist Line HighDeck):
   * - dopóki nie wszystkie widoczne podpola są uzupełnione, zmiana NIE jest
   *   blokowana - {{version}} jest przeliczane na bieżąco (będzie po prostu
   *   niekompletne), a użytkownik dostaje tylko podpowiedź, których pól
   *   jeszcze brakuje (sprawdzanie dopasowania w tabeli mas nie ma sensu dla
   *   niekompletnej wersji),
   * - dopiero gdy wszystkie podpola są uzupełnione: jeśli wynikowa wersja NIE
   *   ma dopasowania w tabeli mas, zmiana jest blokowana (bez pytania) -
   *   podpole wraca do poprzedniej wartości, a użytkownik dostaje komunikat,
   *   że taka kombinacja nie jest obsługiwana; jeśli dopasowanie istnieje,
   *   pojawia się pytanie o potwierdzenie (zmiana wpływa na sekcje mas
   *   13/16/17/18/19) - odmowa cofa zmianę podpola, tak jakby nic się nie
   *   stało.
   * Dla pozostałych zabudów {{version}} jest przeliczane od razu, bez pytania
   * (brak jeszcze danych masowych do aktualizacji).
   */
  private subscribeToVersionSubfields(): void {
    const subfieldKeys = [
      "versionTonnage",
      "versionWheelbase",
      "versionPNR",
      "versionTrunk",
      "versionSideTrunks",
      "versionSpace",
      // Mercedes Benz Tourist Line (zwykły, nie HighDeck)
      "versionPassengersCode",
      "versionMassCode",
      "versionWheelchairCode",
    ];

    subfieldKeys.forEach((key) => {
      const control = this.form.get(key);
      if (!control) return;

      let previousValue = control.value;

      control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((currentValue) => {
          if (currentValue === previousValue) {
            return;
          }
          const revertValue = previousValue;
          previousValue = currentValue;

          if (!this.producerHasMassDataSource()) {
            this.recomputeAndApplyVersion();
            // Zabudowa bez tabeli danych masowych (sekcja 13) może mimo to
            // mieć tabelę 16.2/17.2 (dziś: zwykły MB Tourist Line - klucz
            // {{versionMassCode}}) - odśwież ją bezwarunkowo.
            this.applyMassDataForCurrentVersion(true);
            this.markVersionSubfieldChanged();
            return;
          }
          const line = this.currentVehicleLine();
          if (!line) return;

          // Dopóki nie wszystkie podpola wersji są uzupełnione, sprawdzanie
          // dopasowania w tabeli mas nie ma sensu (wersja jest z definicji
          // niekompletna) - zamiast fałszywego "brak danych masowych",
          // podpowiadamy tylko czego jeszcze brakuje i nie blokujemy zmiany.
          const missingFields = this.getMissingVersionSubfieldLabels();
          if (missingFields.length > 0) {
            this.showMissingVersionSubfieldsHint(missingFields);
            this.recomputeAndApplyVersion();
            this.markVersionSubfieldChanged();
            return;
          }

          // Sprawdź PRZED zapytaniem o potwierdzenie, czy dla wynikowej wersji
          // w ogóle istnieją dane masowe (sekcja 13 ORAZ - o ile zabudowa ma
          // zarejestrowaną taką tabelę - 16.2/17.2) - jeśli któregoś brakuje,
          // blokujemy zmianę od razu, żeby nie dopuścić do rozjazdu między
          // {{version}} a sekcjami mas.
          const prospectiveVersion = this.computeVersionValue();
          const standingPlacesCount = this.form.get(
            "standingPlacesCount",
          )?.value;
          const massData = getMassData(
            line,
            prospectiveVersion,
            standingPlacesCount,
          );

          const baseVehicleVersion = this.form.get("baseVehicleVersion")?.value;
          const axleMassLimit = getAxleMassLimit(
            line,
            this.axleMassLimitLookupKey(line),
            extractGearboxMarker(baseVehicleVersion),
          );
          // Wymagamy dopasowania w tabeli 16.2/17.2 tylko dla zabudów, które
          // w ogóle mają taką tabelę zarejestrowaną (dziś: Iveco) - dla
          // pozostałych (np. Mercedes HighDeck, na razie bez tych danych)
          // brak dopasowania nie blokuje zmiany.
          const axleDataRequired = hasAxleMassLimitTable(line);
          if (!massData || (axleDataRequired && !axleMassLimit)) {
            previousValue = revertValue;
            control.setValue(revertValue, { emitEvent: false });
            this.showMassDataMissingWarning(prospectiveVersion);
            return;
          }

          this.confirmMassRecalculation().then((confirmed) => {
            if (!confirmed) {
              previousValue = revertValue;
              control.setValue(revertValue, { emitEvent: false });
              return;
            }
            this.recomputeAndApplyVersion();
            this.applyMassDataForCurrentVersion(true);
            this.markVersionSubfieldChanged();
          });
        });
    });
  }

  /**
   * Informuje użytkownika, że wybrana kombinacja podpól nie ma odpowiednika
   * w tabeli danych masowych, więc zmiana została zablokowana (cofnięta).
   * Wywoływane tylko, gdy WSZYSTKIE podpola wersji są już uzupełnione (patrz
   * getMissingVersionSubfieldLabels) - inaczej brak dopasowania jest
   * oczywisty (wersja jest jeszcze niekompletna) i nie powinien niepokoić
   * użytkownika w trakcie wypełniania.
   */
  private showMassDataMissingWarning(prospectiveVersion: string): void {
    this.snackBar.open(
      `Brak danych masowych dla wersji "${prospectiveVersion}" - zmiana została zablokowana.`,
      "Zamknij",
      {
        duration: 5000,
        horizontalPosition: "center",
        verticalPosition: "bottom",
      },
    );
  }

  /** Etykiety i nazwy kontrolek podpól wersji, w kolejności prezentacji w formularzu */
  private readonly versionSubfieldDefinitions: Array<{
    visibilityKey:
      | "tonnage"
      | "wheelbase"
      | "pnr"
      | "trunk"
      | "sideTrunks"
      | "space"
      | "passengersCode"
      | "massCode"
      | "wheelchairCode";
    controlName: string;
    label: string;
  }> = [
    { visibilityKey: "tonnage", controlName: "versionTonnage", label: "Tonaż" },
    {
      visibilityKey: "wheelbase",
      controlName: "versionWheelbase",
      label: "Rozstaw",
    },
    { visibilityKey: "pnr", controlName: "versionPNR", label: "P/N" },
    { visibilityKey: "trunk", controlName: "versionTrunk", label: "Bagażnik" },
    {
      visibilityKey: "sideTrunks",
      controlName: "versionSideTrunks",
      label: "Bagażniki boczne",
    },
    {
      visibilityKey: "space",
      controlName: "versionSpace",
      label: "Przestrzeń",
    },
    // Podpola wersji tylko dla zwykłego "Mercedes Benz Tourist Line"
    {
      visibilityKey: "passengersCode",
      controlName: "versionPassengersCode",
      label: "Kod miejsc",
    },
    {
      visibilityKey: "massCode",
      controlName: "versionMassCode",
      label: "Masa",
    },
    {
      visibilityKey: "wheelchairCode",
      controlName: "versionWheelchairCode",
      label: "Wózki inwalidzkie",
    },
  ];

  /**
   * Zwraca etykiety widocznych dla aktualnej zabudowy podpól wersji, które są
   * jeszcze puste - zanim wszystkie nie są uzupełnione, sprawdzanie
   * dopasowania w tabeli mas nie ma sensu (wersja jest z definicji
   * niekompletna).
   */
  private getMissingVersionSubfieldLabels(): string[] {
    return this.versionSubfieldDefinitions
      .filter((f) => this.visibleVersionSubfields[f.visibilityKey])
      .filter((f) => !this.form.get(f.controlName)?.value)
      .map((f) => f.label);
  }

  /** Podpowiada użytkownikowi, które podpola wersji trzeba jeszcze uzupełnić */
  private showMissingVersionSubfieldsHint(missingLabels: string[]): void {
    this.snackBar.open(
      `Uzupełnij jeszcze: ${missingLabels.join(", ")}.`,
      "Zamknij",
      {
        duration: 4000,
        horizontalPosition: "center",
        verticalPosition: "bottom",
      },
    );
  }

  /**
   * Sprawdza czy wszystkie wymagane (widoczne dla aktualnej zabudowy) podpola
   * wersji są uzupełnione - jeśli nie, pokazuje podpowiedź i blokuje dalszą
   * akcję. Używane jako warunek wejścia w saveDocument i onHardenClick, żeby
   * nie dało się zapisać ani utwardzić dokumentu z niekompletną wersją.
   */
  private ensureRequiredVersionSubfieldsFilled(): boolean {
    const missingFields = this.getMissingVersionSubfieldLabels();
    if (missingFields.length > 0) {
      this.showMissingVersionSubfieldsHint(missingFields);
      return false;
    }
    return true;
  }

  /**
   * Funkcja odpowiedzialna za walidację, czy rzeczywista masa (13.2) nie jest
   * mniejsza od masy w stanie gotowym do jazdy (13). Puste pola są pomijane.
   */
  private actualMassNotLowerThanRunningOrderValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const toMass = (value: unknown): number | null =>
        value === null ||
        value === undefined ||
        value === "" ||
        isNaN(Number(value))
          ? null
          : Number(value);

      const actualMass = toMass(control.value);
      const massInRunningOrder = toMass(
        this.form?.get("massInRunningOrder")?.value,
      );
      if (actualMass === null || massInRunningOrder === null) {
        return null;
      }

      return actualMass >= massInRunningOrder
        ? null
        : { actualMassTooLow: { min: massInRunningOrder } };
    };
  }

  /**
   * Funkcja odpowiedzialna za ponowne przeliczenie walidacji 13.2 po zmianie 13.
   * Oznacza kontrolkę jako touched, żeby błąd od razu podświetlił pole.
   */
  private revalidateActualMass(): void {
    const control = this.form.get("actualMass");
    if (!control) return;
    control.updateValueAndValidity({ emitEvent: false });
    if (control.invalid) {
      control.markAsTouched();
    }
  }

  /**
   * Funkcja odpowiedzialna za blokadę zapisu/utwardzenia, gdy rzeczywista masa
   * (13.2) jest mniejsza od masy w stanie gotowym do jazdy (13).
   */
  private ensureActualMassValid(): boolean {
    this.revalidateActualMass();
    const error = this.form.get("actualMass")?.getError("actualMassTooLow");
    if (!error) {
      return true;
    }

    this.snackBar.open(
      this._translateService.instant(
        "Rzeczywista masa (13.2) nie może być mniejsza niż masa w stanie gotowym do jazdy (13).",
      ),
      this._translateService.instant("Zamknij"),
      {
        duration: 5000,
        horizontalPosition: "center",
        verticalPosition: "bottom",
      },
    );
    return false;
  }

  /** Ujednolicone oznaczenie "niezapisane zmiany" + odświeżenie podglądu po zmianie podpola wersji */
  private markVersionSubfieldChanged(): void {
    this.hasUnsavedChanges = true;
    this.canPrint = false;
    if (this.isPreviewVisible && this.currentDocument) {
      this.refreshPreview();
    }
  }

  /** Zwraca VehicleLine dla aktualnej kombinacji producent+zabudowa zamówienia, jeśli jest obsługiwana */
  private currentVehicleLine(): VehicleLine | null {
    const key = buildProducerConversionKey(
      this.order?.producent?.name,
      this.order?.conversion?.name,
    );
    return resolveVehicleLine(key);
  }

  /**
   * Wylicza wartości pól zależnych od haka holowniczego (16.4/17.4/18.3/
   * 18.4/19) dla podanego zamówienia - patrz utils/tow-hook.ts. Zwraca null,
   * gdy hak nie jest aktywny (checked/required w order.options) albo
   * zabudowa/masa pojazdu nie pozwalają policzyć wyniku.
   */
  private getTowHookMassValues(order: any): TowHookMassValues | null {
    if (!isTowHookSelected(order?.options)) {
      return null;
    }
    const line = resolveVehicleLine(
      buildProducerConversionKey(
        order?.producent?.name,
        order?.conversion?.name,
      ),
    );
    return resolveTowHookMassValues(line, order?.vehicleWeight?.weight);
  }

  /** Czy dla aktualnej kombinacji producent+zabudowa mamy zarejestrowane źródło danych masowych */
  private producerHasMassDataSource(): boolean {
    const line = this.currentVehicleLine();
    return line !== null && hasMassDataTable(line);
  }

  /**
   * Pyta użytkownika o potwierdzenie aktualizacji sekcji mas po zmianie wersji.
   * Reużywa istniejący współdzielony DialogComponent (patrz resetDocumentTemplate).
   */
  private async confirmMassRecalculation(): Promise<boolean> {
    const { DialogComponent } =
      await import("app/shared/dialog/dialog.component");
    const dialogRef = this.dialog.open(DialogComponent, {
      width: "420px",
      data: {
        text: "Zmiana opcji wpłynie na aktualizację trzech parametrów związanych z punktami dot. Mas, dział 13 - pola 'Masa w stanie gotowym', 'Masa na osi 1', 'Masa na osi 2'. Kontynuować?",
      },
    });
    const result = await firstValueFrom(dialogRef.afterClosed());
    return result?.status === true;
  }

  /**
   * Klucz główny do tabeli dopuszczalnych mas na osiach (sekcja 16.2/17.2) dla
   * danej zabudowy - patrz getAxleMassLimit. Dla zwykłego Mercedes Benz Tourist
   * Line jest to kod masy pojazdu ({{versionMassCode}}, "C"/"F"), dla
   * pozostałych zabudów - tonaż ({{versionTonnage}}).
   */
  private axleMassLimitLookupKey(line: VehicleLine | null): string {
    if (line === VehicleLine.MercedesTouristLine) {
      return this.form.get("versionMassCode")?.value || "";
    }
    return this.form.get("versionTonnage")?.value || "";
  }

  /**
   * Wypełnia pola sekcji 13 (masa w stanie gotowym) na podstawie danych masowych
   * dla aktualnej wersji pojazdu. Obsługiwane dla zabudów zarejestrowanych w
   * utils/mass-data/vehicle-mass-data.ts (dziś: Iveco Tourist Line, Mercedes
   * Benz Tourist Line HighDeck). Dodatkowo - niezależnie od tabeli sekcji 13 -
   * wypełnia sekcję 16.2/17.2, jeśli zabudowa ma zarejestrowaną tabelę
   * dopuszczalnych mas na osiach (zwykły MB Tourist Line ma dziś tylko ją).
   *
   * @param force - Gdy false (domyślnie), nie nadpisuje pól, które już mają
   *   wartość (np. przywróconą z previousLanguageDraft albo ręcznie wpisaną) -
   *   używane przy cichym, wstępnym wypełnieniu. Gdy true, zawsze nadpisuje -
   *   używane po jawnym potwierdzeniu zmiany wersji przez użytkownika.
   */
  private applyMassDataForCurrentVersion(force = false): void {
    const line = this.currentVehicleLine();
    // Uruchamiamy się, gdy zabudowa ma tabelę danych masowych (sekcja 13)
    // LUB tabelę dopuszczalnych mas na osiach (sekcja 16.2/17.2) - zwykły
    // MB Tourist Line ma dziś tylko tę drugą.
    if (!line || (!hasMassDataTable(line) && !hasAxleMassLimitTable(line))) {
      return;
    }

    if (!force && hasMassDataTable(line)) {
      const currentValue = this.form.get("massInRunningOrder")?.value;
      const hasExistingValue =
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== "";
      if (hasExistingValue) {
        return;
      }
    }

    const version = this.form.get("version")?.value;
    const standingPlacesCount = this.form.get("standingPlacesCount")?.value;
    const massData = hasMassDataTable(line)
      ? getMassData(line, version, standingPlacesCount)
      : null;

    const patch: Record<string, string> = {};

    if (massData) {
      const massAxle2 = massData.axle2Max;
      const massInRunningOrder = massData.massInRunningOrderMax;
      const massAxle1 = massInRunningOrder - massAxle2;

      patch["massInRunningOrder"] = String(massInRunningOrder);
      patch["massAxle2"] = String(massAxle2);
      patch["massAxle1"] = String(massAxle1);
    }

    // Sekcja 16.2 / 17.2 - dopuszczalne masy na osiach (Iveco, MB HighDeck,
    // zwykły MB Tourist Line - patrz vehicle-axle-mass-limits.ts); 17.2 =
    // dokładnie te same wartości co 16.2. Dla zabudów bez zarejestrowanej
    // tabeli axleMassLimit jest null i te pola po prostu nie są patchowane.
    const baseVehicleVersion = this.form.get("baseVehicleVersion")?.value;
    const axleMassLimit = getAxleMassLimit(
      line,
      this.axleMassLimitLookupKey(line),
      extractGearboxMarker(baseVehicleVersion),
    );

    if (axleMassLimit) {
      patch["maxMassAxle1"] = String(axleMassLimit.axle1);
      patch["maxMassAxle2"] = String(axleMassLimit.axle2);
      patch["intendedMaxMassAxle1"] = String(axleMassLimit.axle1);
      patch["intendedMaxMassAxle2"] = String(axleMassLimit.axle2);
    }

    if (Object.keys(patch).length === 0) {
      return;
    }

    this.form.patchValue(patch, { emitEvent: false });
    // patchValue bez emitEvent nie wyzwala valueChanges massInRunningOrder
    this.revalidateActualMass();
  }

  /**
   * Oblicza wartość pola {{version}} na podstawie podpól wersji.
   * Dla Iveco Tourist Line: tonnage + wheelbase + trunk + sideTrunks
   * Dla MB Tourist Line HighDeck: tonnage + wheelbase + P/N + trunk + sideTrunks + space
   */
  computeVersionValue(): string {
    const producentName = this.order?.producent?.name;
    const conversionName = this.order?.conversion?.name;
    const key = buildProducerConversionKey(producentName, conversionName);

    const tonnage = this.form.get("versionTonnage")?.value || "";
    const wheelbase = this.form.get("versionWheelbase")?.value || "";
    const pnr = this.form.get("versionPNR")?.value || "";
    const trunk = this.form.get("versionTrunk")?.value || "";
    const sideTrunks = this.form.get("versionSideTrunks")?.value || "";
    const space = this.form.get("versionSpace")?.value || "";

    // Iveco Tourist Line: tonnage + wheelbase + P + trunk + sideTrunks (bez P/N i space)
    if (key === VehicleLine.IvecoTouristLine) {
      return `${tonnage}${wheelbase}${trunk}${sideTrunks}`;
    }

    // Mercedes Benz Tourist Line HighDeck: tonnage + wheelbase + P/N + trunk + sideTrunks + space
    if (key === VehicleLine.MercedesTouristLineHighDeck) {
      return `${tonnage}${wheelbase}${pnr}${trunk}${sideTrunks}${space}`;
    }

    // Mercedes Benz Tourist Line (zwykły): passengersCode + massCode + wheelchairCode
    // (patrz utils/mercedes-tourist-line-version.ts)
    if (key === VehicleLine.MercedesTouristLine) {
      const passengersCode =
        this.form.get("versionPassengersCode")?.value || "";
      const massCode = this.form.get("versionMassCode")?.value || "";
      const wheelchairCode =
        this.form.get("versionWheelchairCode")?.value || "";
      return `${passengersCode}${massCode}${wheelchairCode}`;
    }

    // Dla pozostałych zabudów zwróć ręcznie wpisaną wartość pola version
    return this.form.get("version")?.value;
  }

  /**
   * Sprawdza czy zmienna jest podpolem wersji.
   */
  isVersionSubfield(variable: CertificateOfConformityVariable): boolean {
    const subfieldKeys = [
      "{{versionTonnage}}",
      "{{versionWheelbase}}",
      "{{versionPNR}}",
      "{{versionTrunk}}",
      "{{versionSideTrunks}}",
      "{{versionSpace}}",
      "{{versionPassengersCode}}",
      "{{versionMassCode}}",
      "{{versionWheelchairCode}}",
    ];
    return subfieldKeys.includes(variable.key);
  }

  /**
   * Opcje rozstawu osi dla aktualnej zabudowy - aktualizowane przy ładowaniu
   * zamówienia. Źródłem jest getWheelbaseSelectOptions (wheelbase-utils.ts) -
   * lista faktycznie zadeklarowanych wariantów dla danej zabudowy, a nie
   * dowolnie przyjęty zakres numeryczny.
   */
  wheelbaseOptions: Array<{ value: string; label: string }> = [];

  /**
   * Tabelka informacyjna (kod W / opis) dla aktualnej zabudowy+tonażu -
   * pokazywana w formularzu przy polu "Rozstaw", gdy {{versionWheelbase}}
   * nie zostało ustawione automatycznie (patrz getWheelbaseReferenceTable
   * w wheelbase-utils.ts) - pomaga wybrać właściwy wariant ręcznie.
   */
  wheelbaseReferenceTable: Array<{ code: string; description: string }> = [];

  /**
   * Aktualizuje cache podpól wersji na podstawie zamówienia.
   */
  updateVersionSubfieldsCache(): void {
    const producentName = this.order?.producent?.name;
    const conversionName = this.order?.conversion?.name;
    const key = buildProducerConversionKey(producentName, conversionName);
    const line = resolveVehicleLine(key);
    const tonnage = this.computeTonnageCode(this.order);
    const passengersCount = this.order?.places?.variants?.[0]?.passengersCount;
    const pilotsCount = this.order?.places?.variants?.[0]?.pilotsCount;

    this.wheelbaseOptions = getWheelbaseSelectOptions(
      line,
      tonnage,
      passengersCount,
      pilotsCount,
    );
    this.wheelbaseReferenceTable = getWheelbaseReferenceTable(line, tonnage);

    // Aktualizuj widoczne podpola
    if (line === VehicleLine.IvecoTouristLine) {
      this.visibleVersionSubfields = {
        tonnage: true,
        wheelbase: true,
        pnr: false,
        trunk: true,
        sideTrunks: true,
        space: false,
        passengersCode: false,
        massCode: false,
        wheelchairCode: false,
      };
      this.showVersionSubfields = true;
    } else if (line === VehicleLine.MercedesTouristLineHighDeck) {
      this.visibleVersionSubfields = {
        tonnage: true,
        wheelbase: true,
        pnr: true,
        trunk: true,
        sideTrunks: true,
        space: true,
        passengersCode: false,
        massCode: false,
        wheelchairCode: false,
      };
      this.showVersionSubfields = true;
    } else if (line === VehicleLine.MercedesTouristLine) {
      // Zwykły Mercedes Benz Tourist Line (NIE HighDeck) - wersja składa się
      // z 3 własnych kodów zamiast tonażu/rozstawu osi (patrz
      // utils/mercedes-tourist-line-version.ts i computeVersionValue).
      this.visibleVersionSubfields = {
        tonnage: false,
        wheelbase: false,
        pnr: false,
        trunk: false,
        sideTrunks: false,
        space: false,
        passengersCode: true,
        massCode: true,
        wheelchairCode: true,
      };
      this.showVersionSubfields = true;
    } else {
      this.visibleVersionSubfields = {
        tonnage: false,
        wheelbase: false,
        pnr: false,
        trunk: false,
        sideTrunks: false,
        space: false,
        passengersCode: false,
        massCode: false,
        wheelchairCode: false,
      };
      this.showVersionSubfields = false;
    }

    this.updateVersionSubfieldValidators();
  }

  /**
   * Ustawia Validators.required na kontrolkach podpól wersji widocznych dla
   * aktualnej zabudowy (patrz visibleVersionSubfields) i czyści walidację na
   * pozostałych. Wywoływane po każdej aktualizacji visibleVersionSubfields
   * (updateVersionSubfieldsCache), bo widoczność zależy od zamówienia i może
   * się zmienić dopiero po jego wczytaniu (formularz jest budowany wcześniej,
   * bez walidatorów - patrz buildForm).
   */
  private updateVersionSubfieldValidators(): void {
    this.versionSubfieldDefinitions.forEach((f) => {
      const control = this.form.get(f.controlName);
      if (!control) return;
      control.setValidators(
        this.visibleVersionSubfields[f.visibilityKey]
          ? [Validators.required]
          : [],
      );
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Wyznacza kod tonażu ({{versionTonnage}}, np. "62") z masy pojazdu
   * zamówienia. Wspólna logika dla computeVariableValue (case
   * {{versionTonnage}}) i applyWheelbaseValuesFromOrderOptions - ta druga
   * potrzebuje tonażu ZANIM {{versionTonnage}} trafi do formularza (patrz
   * autoFillFromOrder - wheelbase patch liczony jest przed patchValue), więc
   * nie może po prostu odczytać wartości z form.get("versionTonnage").
   */
  private computeTonnageCode(order: any): string {
    const weight = order?.vehicleWeight?.weight;
    if (weight == null) return "";
    const str = String(weight);
    // ucinamy 2 ostatnie znaki, jeśli są
    return str.length > 2 ? str.slice(0, -2) : "";
  }

  /**
   * Ustawia wartości pól w formularzu na podstawie danych z wheelbases.
   * Wywoływana automatycznie przy ładowaniu zamówienia.
   * Deleguje do wheelbase-utils dla logiki biznesowej.
   */
  private applyWheelbaseValuesFromOrderOptions(): Record<
    string,
    string
  > | null {
    const patchValues = resolveWheelbaseFormValues({
      orderOptions: this.order?.options,
      producentName: this.order?.producent?.name,
      conversionName: this.order?.conversion?.name,
      tonnage: this.computeTonnageCode(this.order),
      passengersCount: this.order?.places?.variants?.[0]?.passengersCount,
      pilotsCount: this.order?.places?.variants?.[0]?.pilotsCount,
    });

    if (patchValues && Object.keys(patchValues).length > 0) {
      this.form.patchValue(patchValues, { emitEvent: false });
      return patchValues;
    }
    return null;
  }

  /**
   * Zmienna wersji ({{version}}) - używana w template.
   */
  versionVariable: CertificateOfConformityVariable | undefined =
    CERTIFICATE_OF_CONFORMITY_VARIABLES.find((v) => v.key === "{{version}}");

  /**
   * Cache widocznych podpól wersji - aktualizowany przy zmianie zamówienia.
   */
  visibleVersionSubfields = {
    tonnage: false,
    wheelbase: false,
    pnr: false,
    trunk: false,
    sideTrunks: false,
    space: false,
    passengersCode: false,
    massCode: false,
    wheelchairCode: false,
  };

  /**
   * Czy pokazywać podpola wersji - cache.
   */
  showVersionSubfields = false;

  /**
   * Zwraca opcje dla podpola wersji na podstawie nazwy kontrolki.
   */
  getVersionSubfieldOptions(
    controlName: string,
  ): Array<{ value: string; label: string }> {
    const variable = CERTIFICATE_OF_CONFORMITY_VARIABLES.find(
      (v) => this.toControlName(v.key) === controlName,
    );
    return variable?.selectOptions || [];
  }

  /**
   * Zwraca tooltip dla podpola wersji.
   */
  getVersionSubfieldTooltip(controlName: string): any {
    const variable = CERTIFICATE_OF_CONFORMITY_VARIABLES.find(
      (v) => this.toControlName(v.key) === controlName,
    );
    if (variable) {
      return this.getTooltipForVariable(variable);
    }
    return "";
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Funkcja odpowiedzialna za budowanie reaktywnego formularza z kontrolkami
   * dla każdej zmiennej szablonu i pola wyboru języka.
   */
  private buildForm(): FormGroup {
    const controls: Record<string, any> = {
      language: [{ value: "", disabled: true }, Validators.required],
    };
    CERTIFICATE_OF_CONFORMITY_VARIABLES.forEach((v) => {
      // mat-select z [multiple] wymaga tablicy jako wartości - pusty string
      // powoduje błąd w czasie działania ("Value must be an array...")
      const emptyValue = v.inputType === "multiselect" ? [] : "";
      controls[this.toControlName(v.key)] = [
        { value: emptyValue, disabled: this.isVariableDisabled(v) },
      ];
    });
    return this.fb.group(controls);
  }

  /**
   * Sprawdza czy pole powinno być wyłączone (disabled).
   */
  isVariableDisabled(variable: CertificateOfConformityVariable): boolean {
    // Dokument utwardzony - wszystkie pola są zablokowane, niezależnie od
    // pozostałych reguł
    if (this.isHardened) {
      return true;
    }

    // Superadmin zawsze może edytować
    if (this.userRole === "superadmin") {
      return false;
    }

    // Jeśli nie ma disabled: true, pole jest edytowalne
    if (!variable.disabled) {
      return false;
    }

    const controlName = this.toControlName(variable.key);
    const dbValue = this.fieldValues?.[controlName];
    const hadValueInDb =
      dbValue !== undefined && dbValue !== null && dbValue !== "";

    // Pole ma też wartość ustaloną automatycznie przy wczytaniu zamówienia
    // (formatter: computed, value "na sztywno", orderPath, rozstaw osi) -
    // traktujemy to tak samo, jakby wartość była już zapisana w bazie.
    // Uwaga: cache jest odświeżany tylko przy wczytaniu zamówienia, nie przy
    // każdej zmianie w formularzu - dzięki temu pole nie blokuje się w trakcie
    // ręcznego wpisywania wartości.
    const autoResolvedValue = this.autoResolvedValuesCache[controlName];
    const hasAutoResolvedValue =
      autoResolvedValue !== undefined &&
      autoResolvedValue !== null &&
      autoResolvedValue !== "";

    return hadValueInDb || hasAutoResolvedValue;

    //     if (this.loadedFieldValuesByLanguage) {
    //   console.log(this.loadedFieldValuesByLanguage);
    //   const loadedValues =
    //     this.loadedFieldValuesByLanguage[this.activeLanguage || ""] || {};
    //   const dbValue = loadedValues[controlName];

    //   // Pole wyłączone TYLKO gdy w bazie była niepusta wartość
    //   const hadValueInDb =
    //     dbValue !== undefined && dbValue !== null && dbValue !== "";
    //   return hadValueInDb;
    // }

    // Sprawdź czy w formularzu jest niepusta wartość
    // const controlName = this.toControlName(variable.key);
    // const control = this.form?.get(controlName);
    // const currentValue = control?.value;

    // Pole wyłączone TYLKO gdy w formularzu jest niepusta wartość
    // const hasValue =
    //   currentValue !== undefined &&
    //   currentValue !== null &&
    //   currentValue !== "";

    // return hasValue;
  }

  /**
   * Dla superadmin: pole orderDerived uznajemy za nadpisane,
   * jeżeli jest już zapisane w rekordzie lub zostało ręcznie zmodyfikowane.
   */
  isOrderDerivedOverridden(variable: CertificateOfConformityVariable): boolean {
    if (!this.isSuperadmin() || !variable.orderDerived) {
      return false;
    }

    const controlName = this.toControlName(variable.key);
    const language =
      this.form.get("language")?.value || this.activeLanguage || "";
    const persistedOrderDerived = language
      ? this.persistedOrderDerivedValuesByLanguage[language] || {}
      : {};

    const hasPersistedValue = Object.prototype.hasOwnProperty.call(
      persistedOrderDerived,
      controlName,
    );
    const wasEdited = !!this.form.get(controlName)?.dirty;

    return hasPersistedValue || wasEdited;
  }

  private isSuperadmin(): boolean {
    return this.userRole === "superadmin";
  }

  /**
   * Synchronizuje stan enable/disable kontrolek po ustaleniu roli użytkownika.
   */
  private applyVariableControlStates(): void {
    CERTIFICATE_OF_CONFORMITY_VARIABLES.forEach((variable) => {
      const control = this.form.get(this.toControlName(variable.key));
      if (!control) {
        return;
      }

      if (this.isVariableDisabled(variable)) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  /**
   * Funkcja odpowiedzialna za pobranie listy dostępnych języków.
   */
  private loadLanguages(): void {
    this.languageService.getLanguages().subscribe((data: any) => {
      this.languages = data;
    });
  }

  /**
   * Funkcja odpowiedzialna za pobranie danych zamówienia i wstępne
   * wypełnienie pól formularza wartościami z zamówienia.
   */
  private loadOrder(id: string): void {
    this.isLoadingOrder = true;
    this.orderService.getSingleOrder(id).subscribe({
      next: async (order: any) => {
        this.order = order;
        this.updateVersionSubfieldsCache(); // Aktualizuj cache podpól
        // Czekamy na zakończenie autoFillFromOrder (w tym wyliczenie {{version}}),
        // zanim ustawimy domyślny język - inaczej restoreOrInitializeLanguageValues
        // mogłoby nadpisać świeżo wyliczone wartości pustymi (wyścig promisów)
        await this.autoFillFromOrder(order);

        if (!this.form.get("language")?.value) {
          this.form.patchValue({ language: "GB" });
        }
        this.isLoadingOrder = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error("Błąd pobierania zamówienia:", err);
        this.isLoadingOrder = false;
      },
    });
  }

  /**
   * Funkcja odpowiedzialna za automatyczne uzupełnienie pól formularza
   * wartościami pobranymi z obiektu zamówienia.
   */
  private async autoFillFromOrder(order: any): Promise<void> {
    const patch: Record<string, any> = {};

    for (const v of CERTIFICATE_OF_CONFORMITY_VARIABLES) {
      // if (!v.orderPath && v.value === undefined) {
      //   continue;
      // }

      // {{version}} liczymy na końcu - zależy od podpól (tonaż, rozstaw osi
      // itd.), które w tym miejscu pętli mogą jeszcze nie mieć finalnych
      // wartości w formularzu (patchValue niżej jeszcze się nie wykonał)
      if (v.key === "{{version}}") {
        continue;
      }

      const value = await this.resolveVariableValue(order, v);
      if (value !== null && value !== undefined && value !== "") {
        patch[this.toControlName(v.key)] = value;
      }
    }
    // Automatycznie ustaw wartości pól na podstawie wybranego wheelbase z order.options
    const wheelbasePatch = this.applyWheelbaseValuesFromOrderOptions();

    // Zapamiętaj automatycznie ustalone wartości - używane w isVariableDisabled
    this.autoResolvedValuesCache = { ...patch, ...(wheelbasePatch ?? {}) };

    this.form.patchValue(patch, { emitEvent: false });

    // {{version}} liczymy dopiero teraz, gdy podpola (w tym versionWheelbase
    // ustawiane powyżej z obiektu wheelbases) mają już finalne wartości
    // w formularzu - inaczej wynik byłby liczony na nieaktualnych danych.
    this.recomputeAndApplyVersion();

    // Wstępne (ciche, bez okna potwierdzenia) wypełnienie sekcji 13 danymi
    // masowymi dla wyliczonej wersji - okno potwierdzenia dotyczy tylko
    // późniejszych, ręcznych zmian podpól wersji (patrz subscribeToVersionSubfields)
    this.applyMassDataForCurrentVersion();

    // Zastosuj stany enable/disable po wypełnieniu wartościami
    // (disabled zależy teraz od tego czy pole ma wartość)
    this.applyVariableControlStates();
  }

  /**
   * Funkcja odpowiedzialna za obsługę przełączenia języka dokumentu.
   */
  private async onLanguageChanged(language: string): Promise<void> {
    if (!language) {
      return;
    }

    if (this.activeLanguage) {
      this.draftValuesByLanguage[this.activeLanguage] =
        this.getCurrentVariableValues();
    }

    await this.restoreOrInitializeLanguageValues(language);
    this.activeLanguage = language;
    this.loadSavedDocumentForLanguage(language);
  }

  /**
   * Funkcja odpowiedzialna za odtworzenie wartości pól dla wybranego języka
   * lub inicjalizację nowych pól na podstawie danych zamówienia.
   * Pola oznaczone jako requiredTranslation nie są kopiowane przy zmianie języka
   * (podobnie jak flexible) - mają osobne wartości per język.
   */
  private async restoreOrInitializeLanguageValues(
    language: string,
  ): Promise<void> {
    const patch: Record<string, any> = {};

    const existingDraft = this.draftValuesByLanguage[language];
    if (existingDraft) {
      this.form.patchValue(existingDraft, { emitEvent: false });
      // Zastosuj stany enable/disable po odtworzeniu wartości
      this.applyVariableControlStates();
      return;
    }

    const previousLanguageDraft =
      this.activeLanguage && this.draftValuesByLanguage[this.activeLanguage]
        ? this.draftValuesByLanguage[this.activeLanguage]
        : null;

    // Sekwencyjnie (for...of + await), żeby resolveVariableValue zdążyło się
    // rozwiązać przed patchValue niżej - poprzednia wersja (forEach + async)
    // nie czekała na te promisy, więc patch mógł zostać zaaplikowany
    // z niekompletnymi danymi.
    for (const variable of CERTIFICATE_OF_CONFORMITY_VARIABLES) {
      // {{version}} liczymy na końcu - patrz autoFillFromOrder
      if (variable.key === "{{version}}") {
        continue;
      }

      const controlName = this.toControlName(variable.key);

      // Pola requiredTranslation mają osobne wartości per język (nie kopiujemy)
      if (variable.requiredTranslation) {
        // Sprawdź czy mamy zapisaną wartość w persistedTranslations
        const persistedTranslation =
          this.persistedTranslations[language]?.[controlName];
        if (persistedTranslation !== undefined) {
          patch[controlName] = persistedTranslation;
        } else if (this.order) {
          // Jeśli nie ma zapisanego tłumaczenia, użyj wartości z zamówienia
          patch[controlName] = await this.resolveVariableValue(
            this.order,
            variable,
          );
        }
        continue;
      }

      if (
        !variable.flexible &&
        previousLanguageDraft?.[controlName] !== undefined
      ) {
        patch[controlName] = previousLanguageDraft[controlName];
        continue;
      }

      if (this.order) {
        // Pola bez value/orderPath/formatter (np. massInRunningOrder, massAxle1,
        // massAxle2) zwracają tu zawsze "" - pomijamy taki wynik, żeby nie
        // nadpisać pustką wartości ustawionych już wcześniej w autoFillFromOrder
        // (np. przez applyMassDataForCurrentVersion dla pierwszego, domyślnego
        // języka, gdy nie ma jeszcze zapisanego dokumentu w bazie).
        const resolvedValue = await this.resolveVariableValue(
          this.order,
          variable,
        );
        if (
          resolvedValue !== null &&
          resolvedValue !== undefined &&
          resolvedValue !== ""
        ) {
          patch[controlName] = resolvedValue;
        }
      }
    }

    // Automatycznie ustaw wartości pól na podstawie wybranego wheelbase
    // z order.options - bez tego np. versionWheelbase wróciłoby do pustej
    // wartości przy pierwszym przełączeniu na dany język (nie ma value/
    // orderPath/formatter, więc resolveVariableValue zwróciłoby "")
    const wheelbasePatch = this.order
      ? this.applyWheelbaseValuesFromOrderOptions()
      : null;
    Object.assign(patch, wheelbasePatch ?? {});

    this.form.patchValue(patch, { emitEvent: false });

    // {{version}} liczymy dopiero teraz, gdy podpola mają już finalne wartości
    this.recomputeAndApplyVersion();

    // Wstępne (ciche) wypełnienie sekcji 13 danymi masowymi dla wyliczonej
    // wersji - na wypadek gdyby nie zostały odtworzone z previousLanguageDraft
    this.applyMassDataForCurrentVersion();

    // Zastosuj stany enable/disable po wypełnieniu wartościami
    this.applyVariableControlStates();
  }

  /**
   * Przelicza {{version}} na podstawie aktualnych wartości podpól w formularzu
   * (tonaż, rozstaw osi, P/N, bagażnik, bagażniki boczne, przestrzeń) i patchuje
   * wynik do kontrolki `version`. Musi być wywoływane PO tym, jak wszystkie
   * podpola - w tym versionWheelbase ustawiane automatycznie z obiektu
   * wheelbases - mają już swoje finalne wartości w formularzu, inaczej
   * computeVersionValue() odczyta nieaktualne (jeszcze niepatchnięte) dane.
   */
  private recomputeAndApplyVersion(): void {
    const computedVersion = this.computeVersionValue() ?? "";
    this.form.patchValue({ version: computedVersion }, { emitEvent: false });
    if (computedVersion !== "") {
      this.autoResolvedValuesCache["version"] = computedVersion;
    }
  }

  /**
   * Funkcja odpowiedzialna za pobranie zapisanego rekordu dokumentu dla danego języka.
   */
  private loadSavedDocumentForLanguage(language: string): void {
    this.canPrint = false;
    if (!this.orderId) {
      return;
    }

    this.isLoadingDocument = true;

    this.documentDetailsService
      .getSingleDocumentDetails({
        orderId: this.orderId,
        type: DocumentType.CERTIFICATE_OF_CONFORMITY,
      })
      .subscribe({
        next: (record: DocumentDetailsRecord) => {
          // Zachowaj istniejące szablony z innych języków
          this.existingTemplates = record?.templates || [];
          // Status musi być znany PRZED applyVariableControlStates (isVariableDisabled
          // sprawdza isHardened), inaczej pola utwardzonego dokumentu zostałyby
          // błędnie odblokowane
          this.documentStatus = record?.status ?? null;
          const template = getTemplateForLanguage(
            this.existingTemplates,
            language,
          );
          if (template) {
            this.canPrint = true;
          }

          const persistedOrderDerivedValues = this.pickOrderDerivedFieldValues(
            record?.fieldValues || {},
          );
          this.persistedOrderDerivedValuesByLanguage[language] =
            persistedOrderDerivedValues;

          const loadedFieldValues = this.normalizeLoadedFieldValues(
            record?.fieldValues || {},
          );
          this.fieldValues = { ...loadedFieldValues };
          this.form.patchValue(loadedFieldValues, { emitEvent: false });

          // Załaduj tłumaczenia dla pól requiredTranslation
          this.loadTranslationsFromFieldValues(language, loadedFieldValues);

          this.rawRenderedHtml = template?.document || null;
          this.draftValuesByLanguage[language] = {
            ...loadedFieldValues,
          };

          // Ustaw currentDocument z szablonu jeśli istnieje
          if (template?.documentTemplate) {
            this.currentDocument = {
              _id: record.documentId || "",
              language,
              type: DocumentType.CERTIFICATE_OF_CONFORMITY,
              content: template.documentTemplate,
            } as Document;
            // Odśwież podgląd jeśli jest widoczny
            if (this.isPreviewVisible) {
              this.refreshPreview();
            }
            this.isLoadingDocument = false;
            // Zastosuj stany enable/disable po załadowaniu zapisanych wartości
            this.applyVariableControlStates();
            return;
          }

          // Zastosuj stany enable/disable po załadowaniu zapisanych wartości
          this.applyVariableControlStates();

          // Brak szablonu w rekordzie – pobierz szablon przez documentService
          this.documentService
            .getSingleDocument({
              language,
              type: DocumentType.CERTIFICATE_OF_CONFORMITY,
            })
            .subscribe({
              next: (document: Document) => {
                this.currentDocument = document;
                // Odśwież podgląd jeśli jest widoczny
                if (this.isPreviewVisible) {
                  this.refreshPreview();
                }
              },
              error: () => {
                // Fallback: spróbuj ponownie
                this.documentService
                  .getSingleDocument({
                    language,
                    type: DocumentType.CERTIFICATE_OF_CONFORMITY,
                  })
                  .subscribe({
                    next: (document: Document) => {
                      this.currentDocument = document;
                      // Odśwież podgląd jeśli jest widoczny
                      if (this.isPreviewVisible) {
                        this.refreshPreview();
                      }
                    },
                  });
              },
              complete: () => {
                this.isLoadingDocument = false;
                // Zastosuj stany enable/disable po zakończeniu ładowania
                this.applyVariableControlStates();
              },
            });
        },
        error: (error: HttpErrorResponse) => {
          if (error.status !== 404) {
            console.error("Błąd pobierania dokumentu:", error);
          } else {
            // Rekord nie istnieje - wyczyść przechowywane szablony
            this.existingTemplates = [];
            this.persistedOrderDerivedValuesByLanguage[language] = {};
            this.documentStatus = null;
          }
          this.isLoadingDocument = false;
          // Zastosuj stany enable/disable nawet po błędzie (formularz może mieć wartości domyślne)
          this.applyVariableControlStates();
        },
      });
  }

  /**
   * Zwraca wartość zmiennej szablonu na podstawie pola zamówienia lub logiki obliczeniowej.
   */
  private async resolveVariableValue(
    order: any,
    variable: CertificateOfConformityVariable,
  ): Promise<any> {
    if (variable.formatter === "computed") {
      const value = await this.computeVariableValue(order, variable.key);
      return value;
    }
    if (variable.value !== undefined) {
      // Jeśli value jest obiektem Record (a nie tablicą - patrz niżej), pobierz
      // wartość na podstawie producenta + zabudowy
      if (
        typeof variable.value === "object" &&
        variable.value !== null &&
        !Array.isArray(variable.value)
      ) {
        const producentName = order?.producent?.name;
        const conversionName = order?.conversion?.name;
        // key np. Mercedes Benz City Line
        const key = buildProducerConversionKey(producentName, conversionName);
        const valueMap = variable.value as Record<
          string,
          string | number | string[]
        >;
        const resolved = valueMap[key] ?? variable.valueFallback;
        if (resolved !== undefined) {
          return resolved;
        }
        // Brak dopasowania - mat-select [multiple] wymaga tablicy
        return variable.inputType === "multiselect" ? [] : "";
      }
      // value jest tablicą (statyczna lista dla multiselect) albo skalarem
      return variable.value;
    }

    if (!variable.orderPath) {
      // mat-select z [multiple] wymaga tablicy jako wartości - zwrócenie ""
      // powodowałoby błąd w czasie działania przy patchowaniu formularza
      return variable.inputType === "multiselect" ? [] : "";
    }

    const raw = this.getNestedValue(order, variable.orderPath!);
    if (variable.inputType === "date" && raw) {
      // Jeśli raw to string, przekonwertuj na Date
      if (typeof raw === "string") {
        return this.datePipe.transform(new Date(), "dd.MM.yyyy");
      }
      // Jeśli raw to już Date, zwróć jak jest
      return raw;
    }

    if (variable.formatter === "currency" && raw !== undefined) {
      return raw;
    }

    return raw ?? "";
  }

  /**
   * Funkcja odpowiedzialna za obliczenie wartości zmiennych wymagających dedykowanej logiki biznesowej.
   */
  private async computeVariableValue(order: any, key: string): Promise<any> {
    switch (key) {
      case "{{vinNumber}}": {
        const vinNumber = order?.vinNumber;
        return vinNumber;
      }
      case "{{vehicleData}}": {
        const variantLabel =
          order?.places?.variants?.[0]?.variantName ?? order?.placesCount;

        const parts = [
          order?.producent?.name,
          order?.producent?.type,
          order?.conversion?.name,
          order?.busModel,
          variantLabel,
        ].filter(Boolean);

        return parts.join(" ");
      }
      case "{{vehicleCategory}}": {
        let vehicleCategory = "M3";
        const vehicleWeight = order?.vehicleWeight?.weight;
        if (vehicleWeight < 3500) {
          vehicleCategory = "M1";
        } else if (vehicleWeight < 5000) {
          vehicleCategory = "M2";
        }
        return vehicleCategory;
      }
      case "{{maxLadenMass}}":
      case "{{intendedMaxLadenMass}}": {
        const vehicleWeight = order?.vehicleWeight?.weight;
        return vehicleWeight ?? "";
      }
      // Sekcje 16.4/17.4/18.3/18.4/19 - zależne od haka holowniczego w
      // opcjach zamówienia (checked lub required, patrz utils/tow-hook.ts).
      // Gdy hak nieaktywny (albo zabudowa/masa pojazdu nie pozwalają policzyć
      // wyniku), wszystkie zwracają "N/A".
      case "{{towableCentreAxle}}":
        return this.getTowHookMassValues(order)?.towableCentreAxle ?? "N/A";
      case "{{maxMassCombination}}":
        return this.getTowHookMassValues(order)?.maxMassCombination ?? "N/A";
      case "{{intendedMaxMassCombination}}":
        return (
          this.getTowHookMassValues(order)?.intendedMaxMassCombination ?? "N/A"
        );
      case "{{towableUnbraked}}":
        return this.getTowHookMassValues(order)?.towableUnbraked ?? "N/A";
      case "{{staticMassCoupling}}":
        return this.getTowHookMassValues(order)?.staticMassCoupling ?? "N/A";
      case "{{specificationNumber}}": {
        const countBeforeHardening = order?.orderTitle?.countBeforeHardening;
        const count =
          countBeforeHardening !== undefined &&
          countBeforeHardening !== null &&
          countBeforeHardening !== ""
            ? countBeforeHardening
            : order?.orderTitle?.count;
        const year = order?.orderTitle?.year;
        if (count && year) {
          return `${count}/${year}`;
        }
        return count ?? "";
      }
      case "{{versionTonnage}}": {
        return this.computeTonnageCode(order);
      }

      case "{{versionPNR}}": {
        // Tylko dla Mercedes Benz Tourist Line HighDeck - dla pozostałych
        // zabudów pole nie jest widoczne (patrz visibleVersionSubfields.pnr)
        const producentName = order?.producent?.name;
        const conversionName = order?.conversion?.name;
        const producerKey = buildProducerConversionKey(
          producentName,
          conversionName,
        );
        if (producerKey !== VehicleLine.MercedesTouristLineHighDeck) {
          return "";
        }
        const pilotsCount = order?.places?.variants?.[0]?.pilotsCount;
        return pilotsCount === 1 ? "P" : "N";
      }

      // Kod ilości miejsc - tylko dla zwykłego Mercedes Benz Tourist Line
      // (patrz utils/mercedes-tourist-line-version.ts). Dla pozostałych
      // zabudów pole nie jest widoczne (visibleVersionSubfields.passengersCode).
      case "{{versionPassengersCode}}": {
        const line = resolveVehicleLine(
          buildProducerConversionKey(
            order?.producent?.name,
            order?.conversion?.name,
          ),
        );
        if (line !== VehicleLine.MercedesTouristLine) {
          return "";
        }
        return getMercedesTouristLinePassengersCode(
          order?.places?.variants?.[0],
          order?.options,
        );
      }

      // Kod masy (C/F) - tylko dla zwykłego Mercedes Benz Tourist Line,
      // wyliczany z order.vehicleWeight.weight (nadpisywalny ręcznie).
      case "{{versionMassCode}}": {
        const line = resolveVehicleLine(
          buildProducerConversionKey(
            order?.producent?.name,
            order?.conversion?.name,
          ),
        );
        if (line !== VehicleLine.MercedesTouristLine) {
          return "";
        }
        return getMercedesTouristLineMassCode(order?.vehicleWeight?.weight);
      }

      // Kod obecności wózków inwalidzkich (P/W) - tylko dla zwykłego
      // Mercedes Benz Tourist Line.
      case "{{versionWheelchairCode}}": {
        const line = resolveVehicleLine(
          buildProducerConversionKey(
            order?.producent?.name,
            order?.conversion?.name,
          ),
        );
        if (line !== VehicleLine.MercedesTouristLine) {
          return "";
        }
        return getMercedesTouristLineWheelchairCode(
          order?.places?.variants?.[0],
        );
      }

      case "{{version}}": {
        return this.computeVersionValue();
      }
      case "{{gearboxType}}": {
        const text = await this.getGearboxText(order);
        return text;
      }

      case "{{seatingPositionsCount}}": {
        const variant = order?.places?.variants?.[0];
        const sittingPlacesCount = variant?.sittingPlacesCount || 0;
        const driversCount = variant?.driversCount || 0;
        const pilotsCount = variant?.pilotsCount || 0;
        return sittingPlacesCount + driversCount + pilotsCount;
      }
      case "{{standingPlacesCount}}": {
        const standingPlacesCount =
          order?.places?.variants?.[0]?.standingPlacesCount;
        return standingPlacesCount || 0;
      }
      case "{{vehicleClass}}":
      case "{{vehicleClassSimplified}}": {
        const sittingPlacesCount =
          order?.places?.variants?.[0]?.sittingPlacesCount || 0;
        const standingPlacesCount =
          order?.places?.variants?.[0]?.standingPlacesCount || 0;
        const conversionName = order?.conversion?.name;
        const { vehicleClass, vehicleClassSimplified } = resolveVehicleClass(
          sittingPlacesCount,
          standingPlacesCount,
          conversionName,
        );
        return key === "{{vehicleClass}}"
          ? vehicleClass
          : vehicleClassSimplified;
      }
      case "{{manufactureDate}}": {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const isoDate = `${year}-${month}-${day}`; // "2026-08-14"
        return isoDate;
      }
      case "{{engineCapacity}}": {
        const engineCapacity = order?.engineCapacity;
        return engineCapacity;
      }

      default:
        return "";
    }
  }
  async getGearboxText(order: any): Promise<string> {
    const translations = await firstValueFrom(
      this._translateService.getTranslation("GB"),
    );

    const key = order.gearbox;
    const value = translations?.[key];

    if (!value) {
      if (!key) {
        return "";
      }
      // fallback do bieżącego języka
      return this._translateService.instant(key, {});
    }

    // prosta interpolacja, jeśli kiedyś będziesz miał parametry
    return value;
  }
  /**
   * Funkcja odpowiedzialna za formatowanie kwoty numerycznej jako ciąg z walutą.
   */
  private formatCurrency(value: number | string, currency: string): string {
    return `${Number(value).toLocaleString("pl-PL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  }

  /**
   * Wzorzec placeholdera "brak wartości" dla pól mas (np. {{towableUnbraked}}) -
   * takich wartości NIE traktujemy jako liczbę wymagającą jednostki "kg".
   */
  private static readonly MASS_PLACEHOLDER_PATTERN = /^\s*(n\/?a|-)?\s*$/i;

  /**
   * Dopisuje " kg" do wartości pola masy (patrz `renderAppendKg` w
   * certificate-of-conformity-variables.constants.ts), jeśli wartość nie
   * jest pusta/placeholderem ("N/A", "NA", "-") i nie kończy się już na "kg" -
   * zabezpieczenie przed zapomnieniem jednostki przy ręcznym wpisywaniu.
   */
  private appendKgIfMissing(value: string): string {
    const trimmed = String(value).trim();
    if (
      CertificateOfConformityDocumentComponent.MASS_PLACEHOLDER_PATTERN.test(
        trimmed,
      )
    ) {
      return value;
    }
    if (/kg\s*$/i.test(trimmed)) {
      return value;
    }
    return `${trimmed} kg`;
  }

  /**
   * Funkcja odpowiedzialna za pobranie zagnieżdżonej wartości obiektu na podstawie ścieżki dot-notation.
   */
  private getNestedValue(obj: any, path: string): any {
    if (!obj || !path) {
      return "";
    }
    return path.split(".").reduce((acc: any, key: string) => acc?.[key], obj);
  }

  /**
   * Funkcja odpowiedzialna za konwersję klucza szablonu na nazwę kontrolki formularza.
   */
  toControlName(key: string): string {
    return key.replace("{{", "").replace("}}", "");
  }

  /**
   * Funkcja odpowiedzialna za zwrócenie opcji wyboru dla zmiennej szablonu.
   * @since 13.07.2026
   */
  getSelectOptions(
    variable: CertificateOfConformityVariable,
  ): Array<{ value: string; label: string }> {
    if (variable.selectOptions?.length) {
      return variable.selectOptions;
    }

    return [
      { value: "EUR", label: "EUR" },
      { value: "PLN", label: "PLN" },
    ];
  }

  /**
   * Funkcja odpowiedzialna za blokowanie zmiany wartości pól number przy przewijaniu kółkiem myszy.
   * @since 13.07.2026
   */
  preventNumberScroll(event: WheelEvent): void {
    event.preventDefault();
    (event.target as HTMLInputElement)?.blur();
  }

  /** Dozwolone znaki w polach mas z `renderAppendKg: true` - cyfry, spacja, "kg" (małymi literami) */
  private static readonly MASS_INPUT_ALLOWED_CHAR = /^[0-9 kg]$/;

  /**
   * Blokuje wpisanie znaku innego niż cyfra, spacja albo "k"/"g" (małe litery)
   * w polach mas z `renderAppendKg: true` - żeby nie dało się np. przypadkiem
   * wpisać "Kg", "KG" czy innej litery zamiast liczby.
   */
  restrictMassInputKeypress(event: KeyboardEvent): void {
    if (
      event.key.length === 1 &&
      !CertificateOfConformityDocumentComponent.MASS_INPUT_ALLOWED_CHAR.test(
        event.key,
      )
    ) {
      event.preventDefault();
    }
  }

  /**
   * Analogicznie do restrictMassInputKeypress, ale dla wklejania (paste) -
   * wkleja tylko dozwolone znaki z zawartości schowka, zamiast blokować
   * wklejanie w całości.
   */
  restrictMassInputPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData("text") ?? "";
    const sanitized = pasted
      .split("")
      .filter((char) =>
        CertificateOfConformityDocumentComponent.MASS_INPUT_ALLOWED_CHAR.test(
          char,
        ),
      )
      .join("");
    event.preventDefault();
    document.execCommand("insertText", false, sanitized);
  }

  /**
   * Funkcja odpowiedzialna za resetowanie zapisanego szablonu dokumentu.
   * Dostępna tylko dla superadmin.
   */
  resetDocumentTemplate(): void {
    if (this.userRole !== "superadmin" || !this.orderId) {
      return;
    }

    const language = this.form.get("language")?.value;
    if (!language) {
      return;
    }

    import("app/shared/dialog/dialog.component").then(({ DialogComponent }) => {
      const dialogRef = this.dialog.open(DialogComponent, {
        width: "400px",
        data: {
          text: `Czy na pewno chcesz zresetować szablon?`,
        },
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (result?.status === true) {
          this.isResetting = true;
          this.documentDetailsService
            .resetDocumentDetailsTemplate({
              orderId: this.orderId || "",
              type: DocumentType.CERTIFICATE_OF_CONFORMITY,
            })
            .subscribe({
              next: () => {
                this.isResetting = false;
                this.documentService
                  .getSingleDocument({
                    language,
                    type: DocumentType.CERTIFICATE_OF_CONFORMITY,
                  })
                  .subscribe({
                    next: (document: Document) => {
                      this.currentDocument = document;
                      const rendered = this.renderTemplate(document.content);
                      this.rawRenderedHtml = rendered;
                      this.refreshPreview();
                    },
                    error: (err: HttpErrorResponse) => {
                      console.error(
                        "Błąd pobierania świeżego szablonu po resecie:",
                        err,
                      );
                    },
                  });
              },
              error: (error: HttpErrorResponse) => {
                console.error("Błąd resetowania szablonu dokumentu:", error);
                this.isResetting = false;
              },
            });
        }
      });
    });
  }

  /**
   * Funkcja odpowiedzialna za jawny zapis wersji językowej dokumentu.
   * @param statusOverride - Gdy podane, zapisuje też nowy status dokumentu
   *   (używane przy utwardzaniu - patrz hardenDocument).
   */
  saveDocument(statusOverride?: DocumentStatus): void {
    const language = this.form.get("language")?.value;
    if (!this.orderId || !language) {
      return;
    }
    if (!this.ensureRequiredVersionSubfieldsFilled()) {
      return;
    }
    if (!this.ensureActualMassValid()) {
      return;
    }
    // Jeśli mamy już zapisany currentDocument i rawRenderedHtml, zapisz od razu bez ponownego renderowania
    if (this.currentDocument && this.rawRenderedHtml !== null) {
      const rendered = this.renderTemplate(this.currentDocument.content);
      this.saveDocumentRecord(this.currentDocument, rendered, statusOverride);
      return;
    }

    // Brak podglądu – pobierz szablon, wyrenderuj i zapisz
    this.isSaving = true;
    this.documentService
      .getSingleDocument({
        language,
        type: DocumentType.CERTIFICATE_OF_CONFORMITY,
      })
      .subscribe({
        next: (document: Document) => {
          const rendered = this.renderTemplate(document.content);
          this.rawRenderedHtml = rendered;
          this.currentDocument = document;
          this.saveDocumentRecord(document, rendered, statusOverride);
          this.isSaving = false;
        },
        error: (err: HttpErrorResponse) => {
          console.error("Błąd pobierania szablonu przy zapisie:", err);
          this.isSaving = false;
        },
      });
  }

  /**
   * Funkcja odpowiedzialna za zapis rekordu dokumentu (wersja językowa).
   * Zapisuje szablon dla aktualnego języku, zachowując istniejące szablony innych języków.
   */
  private saveDocumentRecord(
    document: Document,
    renderedHtml: string,
    statusOverride?: DocumentStatus,
  ): void {
    const language = this.form.get("language")?.value;
    if (!this.orderId || !document?._id || !language) {
      return;
    }

    this.isSaving = true;

    const fieldValues = this.getCurrentVariableValues();

    // Przygotuj nowy szablon dla aktualnego języka
    const newTemplate: DocumentTemplate = {
      language,
      documentTemplate: document.content,
      document: renderedHtml,
    };

    // Scal z przechowywanymi szablonami (z innych języków)
    // Jeśli to pierwszy zapis, existingTemplates będzie puste
    const updatedTemplates = upsertTemplateForLanguage(
      [...this.existingTemplates],
      newTemplate,
    );

    // Aktualizuj przechowywane szablony
    this.existingTemplates = updatedTemplates;

    const payload: DocumentDetailsRecord = {
      orderId: this.orderId,
      documentId: document._id,
      type: DocumentType.CERTIFICATE_OF_CONFORMITY,
      fieldValues,
      flexibleFields: CERTIFICATE_OF_CONFORMITY_VARIABLES.filter(
        (v) => v.flexible,
      ).map((v) => this.toControlName(v.key)),
      templates: updatedTemplates,
      ...(statusOverride ? { status: statusOverride } : {}),
    };

    this.documentDetailsService.upsertDocumentDetails(payload).subscribe({
      next: () => {
        this.draftValuesByLanguage[language] = { ...fieldValues };
        this.persistedOrderDerivedValuesByLanguage[language] =
          this.pickOrderDerivedFieldValues(fieldValues || {});
        this.hasUnsavedChanges = false;
        this.canPrint = true;
        this.isSaving = false;
        if (statusOverride) {
          this.documentStatus = statusOverride;
          // Pola muszą zostać natychmiast zablokowane - isVariableDisabled
          // teraz zwraca true dla wszystkich, bo isHardened jest już prawdziwe
          this.applyVariableControlStates();
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error("Błąd zapisu dokumentu:", error);
        this.isSaving = false;
      },
    });
  }

  /**
   * Otwiera dialog utwardzenia dokumentu - wymaga zweryfikowania daty produkcji,
   * a po potwierdzeniu zapisuje dokument ze statusem "hardened" (Utwardzony).
   * Od tej pory dokument jest tylko do podglądu/pobrania PDF.
   */
  async onHardenClick(): Promise<void> {
    const language = this.form.get("language")?.value;
    if (!language || this.isHardened) {
      return;
    }
    if (!this.ensureRequiredVersionSubfieldsFilled()) {
      return;
    }
    if (!this.ensureActualMassValid()) {
      return;
    }

    const { HardenConfirmationDialogComponent } =
      await import("./harden-confirmation-dialog.component");
    const dialogRef = this.dialog.open(HardenConfirmationDialogComponent, {
      width: "440px",
      data: {
        manufactureDate: this.form.get("manufactureDate")?.value || null,
      },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (!result?.confirmed) {
      return;
    }

    if (result.manufactureDate) {
      this.form.patchValue(
        { manufactureDate: result.manufactureDate },
        { emitEvent: false },
      );
    }

    this.saveDocument({ name: "hardened", code: "Utwardzony" });
  }

  /**
   * Funkcja odpowiedzialna za pobranie bieżących wartości pól formularza.
   * Zwraca wartości zmiennych oraz obiekt translations z wartościami pól
   * requiredTranslation dla wszystkich języków.
   */
  private getCurrentVariableValues(): Record<string, any> {
    const values: Record<string, any> = {};
    const language = this.getPrimaryLanguageKey();
    const persistedOrderDerived = language
      ? this.persistedOrderDerivedValuesByLanguage[language] || {}
      : {};

    CERTIFICATE_OF_CONFORMITY_VARIABLES.forEach((variable) => {
      const controlName = this.toControlName(variable.key);

      if (variable.orderDerived && !this.isSuperadmin()) {
        const hasPersistedValue = Object.prototype.hasOwnProperty.call(
          persistedOrderDerived,
          controlName,
        );

        if (hasPersistedValue) {
          values[controlName] = persistedOrderDerived[controlName];
        }
        return;
      }

      if (variable.orderDerived && this.isSuperadmin()) {
        const hasPersistedValue = Object.prototype.hasOwnProperty.call(
          persistedOrderDerived,
          controlName,
        );
        const wasEdited = !!this.form.get(controlName)?.dirty;

        if (!hasPersistedValue && !wasEdited) {
          return;
        }
      }

      values[controlName] = this.form.get(controlName)?.value ?? "";
    });

    // Zbierz tłumaczenia dla pól requiredTranslation ze wszystkich języków
    const allTranslations = this.collectAllTranslations();
    if (Object.keys(allTranslations).length > 0) {
      values[this.REQUIRED_TRANSLATIONS_META_KEY] = allTranslations;
    }

    return values;
  }

  /**
   * Funkcja odpowiedzialna za zebranie tłumaczeń pól requiredTranslation
   * ze wszystkich języków z pamięci podręcznej i aktualnego formularza.
   */
  private collectAllTranslations(): Record<string, Record<string, any>> {
    const translations: Record<string, Record<string, any>> = {};

    // Dodaj istniejące tłumaczenia z pamięci
    Object.keys(this.translationsByLanguage).forEach((lang) => {
      translations[lang] = { ...this.translationsByLanguage[lang] };
    });

    // Dodaj/zaktualizuj tłumaczenia z aktualnego języka
    const currentLanguage = this.getPrimaryLanguageKey();
    if (currentLanguage) {
      const currentTranslations: Record<string, any> = {};
      this.requiredTranslationVariables.forEach((variable) => {
        const controlName = this.toControlName(variable.key);
        currentTranslations[controlName] =
          this.form.get(controlName)?.value ?? "";
      });
      translations[currentLanguage] = currentTranslations;
    }

    return translations;
  }

  /**
   * Funkcja odpowiedzialna za załadowanie tłumaczeń pól requiredTranslation
   * z zapisanych fieldValues dla danego języka.
   */
  private loadTranslationsFromFieldValues(
    language: string,
    fieldValues: Record<string, any>,
  ): void {
    if (!language || !fieldValues) {
      return;
    }

    // Załaduj obiekt translations z fieldValues
    const storedTranslations = fieldValues[this.REQUIRED_TRANSLATIONS_META_KEY];
    if (storedTranslations && typeof storedTranslations === "object") {
      this.persistedTranslations = { ...storedTranslations };
      this.translationsByLanguage = { ...storedTranslations };

      // Załaduj wartości dla aktualnego języka do formularza
      const translationsForLanguage = storedTranslations[language];
      if (translationsForLanguage) {
        const patch: Record<string, any> = {};
        this.requiredTranslationVariables.forEach((variable) => {
          const controlName = this.toControlName(variable.key);
          if (translationsForLanguage[controlName] !== undefined) {
            patch[controlName] = translationsForLanguage[controlName];
          }
        });
        this.form.patchValue(patch, { emitEvent: false });
      }
    }
  }

  /**
   * Zwraca podstawowy klucz języka (np. "PL", "EN") na podstawie aktywnego języka.
   */
  private getPrimaryLanguageKey(): string | null {
    const language = this.form.get("language")?.value;
    return language || this.activeLanguage;
  }

  /**
   * Wyodrębnia z fieldValues tylko te pola, które są orderDerived.
   */
  private pickOrderDerivedFieldValues(
    fieldValues: Record<string, any>,
  ): Record<string, any> {
    const result: Record<string, any> = {};
    CERTIFICATE_OF_CONFORMITY_VARIABLES.forEach((variable) => {
      if (variable.orderDerived) {
        const controlName = this.toControlName(variable.key);
        if (fieldValues[controlName] !== undefined) {
          result[controlName] = fieldValues[controlName];
        }
      }
    });
    return result;
  }

  /**
   * Normalizuje wczytane z bazy wartości pól przed patchValue. Pola multiselect
   * (np. {{vehicleFittedWith}}) wymagają tablicy w mat-select [multiple] -
   * jeśli w rekordzie jest jeszcze stary format (pojedynczy string, sprzed
   * wprowadzenia multiselect), konwertujemy go na tablicę, żeby uniknąć
   * błędu w czasie działania przy wczytywaniu zapisanego dokumentu.
   */
  private normalizeLoadedFieldValues(
    fieldValues: Record<string, any>,
  ): Record<string, any> {
    const normalized: Record<string, any> = { ...fieldValues };

    CERTIFICATE_OF_CONFORMITY_VARIABLES.forEach((variable) => {
      const controlName = this.toControlName(variable.key);

      // Pola formatter: "computed" (np. versionPNR, versionTonnage) mają już
      // świeżo wyliczoną wartość w formularzu (ustawioną tuż wcześniej przez
      // autoFillFromOrder / restoreOrInitializeLanguageValues). Jeśli w bazie
      // zapisany jest dla nich pusty string (np. stare rekordy sprzed
      // wprowadzenia danej reguły obliczeniowej), pomijamy taki klucz, żeby
      // patchValue nie wyczyścił poprawnej, świeżo policzonej wartości.
      if (
        variable.formatter === "computed" &&
        (normalized[controlName] === "" ||
          normalized[controlName] === null ||
          normalized[controlName] === undefined)
      ) {
        delete normalized[controlName];
      }

      if (variable.inputType !== "multiselect") {
        return;
      }
      const value = normalized[controlName];
      if (value === undefined || Array.isArray(value)) {
        return;
      }
      normalized[controlName] =
        typeof value === "string" && value.trim() !== ""
          ? value
              .split(/[/,]/)
              .map((part) => part.trim())
              .filter(Boolean)
          : [];
    });

    return normalized;
  }

  /**
   * Renderuje szablon HTML zastępując zmienne {{key}} wartościami pól.
   * @param templateContent Surowy HTML szablonu z placeholderami zmiennych
   * @param highlightVariables Gdy true, wartości zmiennych są owijane spanem z czerwonym kolorem
   */
  private renderTemplate(
    templateContent: string,
    highlightVariables = false,
  ): string {
    if (!templateContent) {
      return "";
    }

    let rendered = templateContent;

    CERTIFICATE_OF_CONFORMITY_VARIABLES.forEach((variable) => {
      const controlName = this.toControlName(variable.key);
      let value = this.form.get(controlName)?.value ?? "";

      // Pola multiselect (np. {{vehicleFittedWith}}) trzymają wartość jako
      // tablicę - do szablonu wstawiamy ją jako listę rozdzieloną przecinkami
      if (Array.isArray(value)) {
        value = value.join(", ");
      }

      if (variable.renderAsCurrency) {
        const currency =
          this.form.get(this.toControlName("{{currency}}"))?.value || "EUR";
        if (value !== "" && value !== null && value !== undefined) {
          value = this.formatCurrency(value, currency);
        }
      }

      if (
        variable.renderAppendKg &&
        value !== "" &&
        value !== null &&
        value !== undefined
      ) {
        value = this.appendKgIfMissing(value);
      }

      // Globalne zastąpienie wszystkich wystąpień klucza
      const displayValue = highlightVariables
        ? `<span style="color:red;font-weight:700;">${value}</span>`
        : value;

      if (variable.hidden && variable.key === "{{podpis}}") {
        rendered = rendered
          .split(variable.key)
          .join(
            `<img src="${this.SIGNATURE_IMAGE_PATH}" alt="Podpis" style="height: 40px;" />`,
          );
      } else {
        rendered = rendered.split(variable.key).join(String(displayValue));
      }
    });

    return rendered;
  }

  /**
   * Wyświetla podgląd dokumentu w iframe.
   * Jeśli currentDocument już istnieje, renderuje od razu.
   * W przeciwnym razie pobiera szablon z API.
   */
  previewDocument(): void {
    // Toggle: jeśli podgląd jest widoczny – ukryj go
    if (this.isPreviewVisible) {
      this.isPreviewVisible = false;
      this.previewHtml = null;
      return;
    }

    const language = this.form.get("language")?.value;
    if (!language) {
      return;
    }

    if (this.currentDocument) {
      this.refreshPreview();
      this.isPreviewVisible = true;
    } else {
      this.documentService
        .getSingleDocument({
          language,
          type: DocumentType.CERTIFICATE_OF_CONFORMITY,
        })
        .subscribe({
          next: (doc: Document) => {
            this.currentDocument = doc;
            this.refreshPreview();
            this.isPreviewVisible = true;
          },
          error: (err: HttpErrorResponse) => {
            console.error("Błąd pobierania szablonu przy podglądzie:", err);
          },
        });
    }
  }

  /**
   * Odświeża podgląd iframe bez ponownego pobierania szablonu.
   */
  private refreshPreview(): void {
    if (!this.currentDocument) {
      return;
    }

    const rendered = this.renderTemplate(this.currentDocument.content, true);
    const fullHtml = this.buildPreviewHtml(rendered);
    this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(fullHtml);
  }

  /**
   * Buduje pełny dokument HTML do wyświetlenia w iframe.
   * Używa wspólnych stylów DOCUMENT_STYLES.
   */
  private buildPreviewHtml(content: string): string {
    return `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8">
  <style>${this.DOCUMENT_STYLES}</style>
</head>
<body>${content}</body>
</html>`;
  }

  /**
   * Funkcja odpowiedzialna za pobranie dokumentu do formatu PDF
   * @since 18.08.2026
   */
  downloadPdfMake(): void {
    if (!this.currentDocument?._id) {
      console.error("Brak documentId do pobrania PDF");
      return;
    }
    const orderId = this.orderId as string;

    this.documentDetailsService.downloadPdfMake(orderId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const vinNumber = this.order?.vinNumber || this.currentDocument!._id;
        const timestamp = this.datePipe.transform(new Date(), "dd-MM-yyyy");
        link.download = `COC-${vinNumber}-${timestamp}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (err: HttpErrorResponse) => {
        console.error("Błąd pobierania PDF (pdfmake):", err);
      },
    });
  }

  /**
   * Funkcja odpowiedzialna za zwracanie wyrenderowanego HTML dokumentu dla zadanego języka.
   * Priorytet: cache aktywnego języka -> zapisany rekord -> render z szablonu.
   */
  private getRenderedHtmlForLanguage(
    language: string,
    allowCurrentCache: boolean,
  ): Observable<string> {
    const currentLanguage = this.form.get("language")?.value;

    // Jeśli mamy cache dla aktualnego języka i możemy go użyć
    if (
      allowCurrentCache &&
      currentLanguage === language &&
      this.rawRenderedHtml
    ) {
      return of(this.rawRenderedHtml);
    }

    if (!this.orderId) {
      return this.renderHtmlFromTemplate(language);
    }

    // Spróbuj pobrać zapisany rekord z bazy
    return this.documentDetailsService
      .getSingleDocumentDetails({
        orderId: this.orderId,
        type: DocumentType.CERTIFICATE_OF_CONFORMITY,
      })
      .pipe(
        map((record: DocumentDetailsRecord) => {
          const template = getTemplateForLanguage(record?.templates, language);
          return template?.document || "";
        }),
        switchMap((savedHtml: string) => {
          if (savedHtml) {
            return of(savedHtml);
          }
          return this.renderHtmlFromTemplate(language);
        }),
        catchError(() => this.renderHtmlFromTemplate(language)),
      );
  }

  /**
   * Funkcja odpowiedzialna za renderowanie HTML na podstawie szablonu dla wskazanego języka.
   */
  private renderHtmlFromTemplate(language: string): Observable<string> {
    return this.documentService
      .getSingleDocument({
        language,
        type: DocumentType.CERTIFICATE_OF_CONFORMITY,
      })
      .pipe(map((document: Document) => this.renderTemplate(document.content)));
  }

  /**
   * Funkcja odpowiedzialna za rozwiązanie wartości tooltipa dla zmiennej.
   * Obsługuje zarówno statyczne stringi jak i obiekty Record zależne od producenta + zabudowy.
   * @since 23.07.2026
   * @param variable - definicja zmiennej zawierająca tooltip
   * @returns rozwiązany tekst tooltipa lub pusty string
   */
  getTooltipForVariable(variable: CertificateOfConformityVariable): string {
    let baseTooltip = "";

    // Jeśli tooltip jest stringiem, zwróć go bezpośrednio
    if (typeof variable.tooltip === "string") {
      baseTooltip = variable.tooltip;
    } else if (
      // Jeśli tooltip jest obiektem Record, rozwiąż go na podstawie producenta i zabudowy
      typeof variable.tooltip === "object" &&
      variable.tooltip !== null &&
      !Array.isArray(variable.tooltip)
    ) {
      const producentName = this.order?.producent?.name;
      const conversionName = this.order?.conversion?.name;
      const key = buildProducerConversionKey(producentName, conversionName);
      const tooltipMap = variable.tooltip as Record<string, string>;
      baseTooltip = tooltipMap[key] ?? variable.tooltipFallback ?? "";
    }

    const massRangeHint = this.getMassRangeTooltipHint(variable.key);
    return massRangeHint
      ? `${baseTooltip} ${massRangeHint}`.trim()
      : baseTooltip;
  }

  /**
   * Dla pól sekcji 13 wyliczanych z tabeli mas (massInRunningOrder,
   * massAxle1, massAxle2) dokleja do tooltipa referencyjny zakres min-max
   * wg tabeli zarejestrowanej dla aktualnej zabudowy (patrz
   * vehicle-mass-data.ts), dla aktualnej wersji pojazdu.
   */
  private getMassRangeTooltipHint(variableKey: string): string {
    const relevantKeys = [
      "{{massInRunningOrder}}",
      "{{massAxle1}}",
      "{{massAxle2}}",
    ];
    const line = this.currentVehicleLine();
    if (
      !relevantKeys.includes(variableKey) ||
      !line ||
      !hasMassDataTable(line)
    ) {
      return "";
    }

    const version = this.form.get("version")?.value;
    const standingPlacesCount = this.form.get("standingPlacesCount")?.value;
    const massData = getMassData(line, version, standingPlacesCount);
    if (!massData) {
      return "";
    }

    if (variableKey === "{{massInRunningOrder}}") {
      return `Wg tabeli mas: ${massData.massInRunningOrderMin}–${massData.massInRunningOrderMax} kg.`;
    }
    if (variableKey === "{{massAxle2}}") {
      return `Wg tabeli mas: ${massData.axle2Min}–${massData.axle2Max} kg. Przyjmowana jest wartość maksymalna.`;
    }
    // {{massAxle1}}
    return `Wg tabeli mas (oś 1, informacyjnie): ${massData.axle1RangeMin}–${massData.axle1RangeMax} kg. Wartość pola jest liczona jako różnica masy gotowej do jazdy i osi 2.`;
  }

  /**
   * Otwiera nowe okno przeglądarki z dokumentem gotowym do wydruku.
   * Zawiera stały nagłówek z logo i danymi kontaktowymi.
   * Header powtarza się na każdej stronie druku dzięki strukturze tabeli.
   */
  private openPrintWindow(renderedHtml: string): void {
    const printWindow = window.open("", "_blank");
    const footerBlock = `<div class="print-footer"><span class="page-number"></span></div>`;
    if (!printWindow) {
      console.warn(
        "Przeglądarka zablokowała otwarcie nowego okna. Zezwól na pop-upy dla tej strony.",
      );
      return;
    }

    const title = "Świadectwo zgodności";
    const headerHeightMm = this.PRINT_HEADER_HEIGHT_MM;
    const headerLogoMaxHeightMm = this.PRINT_HEADER_LOGO_MAX_HEIGHT_MM;
    const safeGapMm = this.PRINT_CONTENT_SAFE_GAP_MM;
    const footerHeightMm = this.PRINT_FOOTER_HEIGHT_MM;
    const headerLogoBlock = this.PRINT_HEADER_IMAGE_PATH
      ? `<div class="print-header-logo"><img src="${this.PRINT_HEADER_IMAGE_PATH}" alt="Logo" /></div>`
      : "";
    const headerContactBlock = this.PRINT_HEADER_CONTACT_HTML
      ? `<div class="print-header-contact">${this.PRINT_HEADER_CONTACT_HTML}</div>`
      : "";
    const headerBlock =
      headerLogoBlock || headerContactBlock
        ? `<div class="print-header"><div class="print-header-inner">${headerLogoBlock}${headerContactBlock}</div></div>`
        : "";

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>${title}</title>
  <style>
    @page { size: A4; margin: 10mm 18mm; }
    * { box-sizing: border-box; }
    html, body {
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 10.5pt;
      line-height: 1.5;
      color: #111;
    }

    /* Układ strony: nagłówek jako grupa tabeli */
    .print-layout {
      width: 100%;
      height: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    .print-layout thead { display: table-header-group; }

    /* Style nagłówka */
    .print-header {
      height: calc(${headerHeightMm}mm + ${safeGapMm}mm);
      background: #fff;
      overflow: hidden;
    }
    .print-header-inner {
      height: ${headerHeightMm}mm;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6mm;
      background: #fff;
      border-bottom: 1px solid #e0e0e0;
      padding: 0;
    }
    .print-header-logo {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      min-width: 0;
    }
    .print-header-logo img {
      max-height: ${headerLogoMaxHeightMm}mm;
      align-items: center;
      min-width: 0;
    }
    .print-header-logo img {
      max-height: ${headerLogoMaxHeightMm}mm;
      width: auto;
      display: block;
      margin: 0;
    }
    .print-header-contact {
      flex: 1 1 auto;
      min-width: 0;
      text-align: right;
      font-size: 7.3pt;
      line-height: 1.22;
      color: #444;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .print-header-contact p {
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Style stopki - tylko numeracja */
    .print-footer {
      height: calc(${footerHeightMm}mm + ${safeGapMm}mm);
      display: flex;
      align-items: flex-end;
      justify-content: center;
      background: #fff;
      overflow: hidden;
    }
    .print-footer-inner {
      width: 100%;
      height: ${footerHeightMm}mm;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 9pt;
      color: #555;
      background: #fff;
    }
    .print-content { vertical-align: top; }

    /* Reset marginesów dla paragrafów */
    p { line-height: 1; margin-block-start: 0; margin-block-end: 0; }

    /* Style podglądu zmiennych */
    .variable-highlight { color: red; font-weight: 700; }

    @media print {
      td {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <table class="print-layout" role="presentation">
    <thead>
      <tr>
        <td>${headerBlock}</td>
      </tr>
    </thead>
    <tfoot>
      <tr>
        <td>${footerBlock}</td>
      </tr>
    </tfoot>
    <tbody>
      <tr>
        <td class="print-content">${renderedHtml}</td>
      </tr>
    </tbody>
  </table>
  <script>
    window.onload = function() { window.print(); };
  <\/script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }
}
